import { NotificationMessage } from '@gravitate-js/excalibrr'
import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'

export const useTanks = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const useTankOverviewQuery = (filter) =>
    useQuery({
      queryKey: ['tankOverviewQuery', filter],
      queryFn: ({ queryKey }) =>
        api.post('store/tank/overview', {
          filter: { ...queryKey[1], store_number: queryKey[1]?.store_number?.toString() },
        }),
      refetchOnWindowFocus: false,
    })

  const useTankDetailsQuery = (data) =>
    useQuery({
      queryKey: ['tankDetailsQuery', { store_number: data.store_number, tank_id: parseInt(data.tank_id, 10) }],
      queryFn: ({ queryKey }) =>
        api.post('store/tank/manage', { store_number: data.store_number, tank_id: parseInt(data.tank_id, 10) }),
      refetchOnWindowFocus: false,
    })

  // MODIFY / UPDATE A TANK (can be found within TankInfo.jsx)
  const updateTankMutation = () =>
    useMutation({
      mutationFn: (tank) => api.post('store/tank/update', tank),
      // When mutate is called:
      onSuccess: async (resp, newTank) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['tankOverviewQuery'],
        })

        // Snapshot the previous value

        const previousQueries = queryClient.getQueriesData({
          queryKey: ['tankOverviewQuery'],
        })

        // Optimistically update to the new value
        queryClient.setQueriesData({ queryKey: ['tankOverviewQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.map((tank) => {
              return newTank.tank_id === tank.tank_id && newTank.store_number === tank.store_number
                ? { ...tank, ...newTank, id: newTank.backup_id }
                : tank
            }),
          }
        })
        queryClient.setQueriesData(
          {
            queryKey: [
              'tankDetailsQuery',
              { store_number: newTank.store_number, tank_id: parseInt(newTank.tank_id, 10) },
            ],
          },
          (oldData) => {
            return { ...oldData, ...newTank, id: newTank.backup_id }
          }
        )
        // Return a context with the previous and new todo
        return { previousQueries, newTank }
      },

      onError: (err, newTank, context) => {
        queryClient.setQueriesData({ queryKey: ['tankOverviewQuery'] }, context.previousQueries)
      },
    })

  // NEED TO CREATE ONE AS WELL
  const createTankMutation = () =>
    useMutation({
      mutationFn: (tank) => api.post('store/tank/create', tank),
      // When mutate is called:
      onSuccess: async (response, request) => {
        const newTank = { ...response, ...request }
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['tankOverviewQuery'],
        })

        // Optimistically update to the new value
        queryClient.setQueriesData({ queryKey: ['tankOverviewQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: [newTank, ...oldData.rows],
          }
        })
        NotificationMessage('Success', 'New item created.', false)

        // Return a context with the previous and new todo
        return { newTank }
      },
      onError: (err, newTank, context) => {
        queryClient.setQueriesData({ queryKey: ['tankOverviewQuery'] }, context.previousQueries)
      },
    })

  // DELETE A TANK
  const deleteTankMutation = () =>
    useMutation({
      mutationFn: (tank) => api.post('store/tank/delete', tank),
      // When mutate is called:
      onMutate: async (oldTank) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['tankOverviewQuery'],
        })

        // Snapshot the previous value
        const previousQueries = queryClient.getQueriesData({
          queryKey: ['tankOverviewQuery'],
        })

        // Optimistically update to the new value
        queryClient.setQueriesData({ queryKey: ['tankOverviewQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.filter(
              (row) => row.tank_id !== oldTank.tank_id && row.store_number !== oldTank.store_number
            ),
          }
        })

        // Return a context with the previous and new todo
        return { previousQueries, oldTank }
      },
      onError: (err, newSite, context) => {
        queryClient.setQueriesData({ queryKey: ['tankDeleteQuery'] }, context.previousQueries)
      },
    })

  return { useTankOverviewQuery, useTankDetailsQuery, updateTankMutation, createTankMutation, deleteTankMutation }
}
